/**
 * A gateway's delivered % over the last days, as a small line (one series,
 * 2 px, the chart's own hue) with a dot on each day a verdict blocked it;
 * every day has a hit area that says its date and rates. Days without stats
 * leave a gap. Text stays in text tokens; only the marks carry colour.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenGatewayDay } from '@trafficpulse/shared';

const W = 150;
const H = 28;
const PAD = 4;

const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

export const Sparkline: FC<{ days: DropGenGatewayDay[]; from: string; to: string }> = ({ days, from, to }) => {
  const dates: string[] = [];
  for (let t = Date.parse(from); t <= Date.parse(to); t += 86_400_000) dates.push(new Date(t).toISOString().slice(0, 10));
  const byDate = new Map(days.map((d) => [d.date, d]));
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / Math.max(1, dates.length - 1);
  const y = (v: number) => H - PAD - v * (H - 2 * PAD);
  const points = dates.map((date, i) => ({ date, i, day: byDate.get(date) ?? null }));
  const path = points
    .map((p) => (p.day && p.day.deliveredRate !== null ? `${p.i === 0 || !points[p.i - 1].day || points[p.i - 1].day!.deliveredRate === null ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.day.deliveredRate).toFixed(1)}` : ''))
    .filter(Boolean)
    .join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block" role="img" aria-label="Delivered % by day">
      <line x1={PAD} x2={W - PAD} y1={y(0.85)} y2={y(0.85)} className="stroke-border" strokeWidth={1} strokeDasharray="0" />
      <path d={path} fill="none" className="stroke-primary" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p) =>
        p.day?.blocked ? <circle key={`b${p.i}`} cx={x(p.i)} cy={p.day.deliveredRate !== null ? y(p.day.deliveredRate) : H - PAD} r={4} className="fill-destructive stroke-card" strokeWidth={2} /> : null,
      )}
      {points.map((p) => (
        <rect key={`h${p.i}`} x={x(p.i) - (W - 2 * PAD) / Math.max(1, dates.length - 1) / 2} y={0} width={(W - 2 * PAD) / Math.max(1, dates.length - 1)} height={H} fill="transparent">
          <title>{p.day ? `${ddmm(p.date)}: ${p.day.dropsSent} drops · ${p.day.smsSent.toLocaleString('en-US')} SMS · delivered ${pct(p.day.deliveredRate)} · bot ${pct(p.day.botRate)}${p.day.blocked ? ' · blocked' : ''}` : `${ddmm(p.date)}: no send`}</title>
        </rect>
      ))}
    </svg>
  );
};
